import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { CONTACT, MAPS_LINK, SERVICES, CADEIAS, MUNICIPIOS, waLink } from "@/lib/content";
import { trackContactForm } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const VIZINHOS = MUNICIPIOS.filter((m) => m !== "Pelotas");
const MAPS_EMBED = "https://maps.google.com/maps?q=" + encodeURIComponent("Rua Santos Dumont, 324, Centro, Pelotas - RS, 96020-380") + "&output=embed";

export const MunicipalityPage = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "consent" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!API) return toast.error("Envio indisponível no momento.");
    if (!form.consent) return toast.error("É preciso aceitar a Política de Privacidade.");
    trackContactForm("/municipios/pelotas");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, email: "", service: "", profile: "", location: "Pelotas/RS", website: e.currentTarget.website?.value || "" });
      setSent(true);
      setForm({ name: "", phone: "", message: "", consent: false });
      toast.success("Mensagem enviada! Retornaremos em breve.");
    } catch (err) {
      toast.error("Não foi possível enviar. Fale pelo WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="municipality-page" className="pt-[72px]">
      <section className="grain relative overflow-hidden bg-[#1B2F1D] text-[#F9F8F3]">
        <div className="relative mx-auto max-w-[1400px] px-5 pb-16 pt-16 sm:px-8 md:pt-20">
          <Reveal><Overline light>Atendimento local · Metade Sul do RS</Overline></Reveal>
          <h1 data-testid="municipality-h1" className="mt-6 max-w-3xl font-serif text-4xl font-semibold leading-[1.02] tracking-[-0.03em] sm:text-5xl lg:text-6xl">
            Engenharia e crédito rural <em className="text-[#8FC690]">em Pelotas/RS</em>
          </h1>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#F9F8F3]/80 md:text-lg">
              Nossa sede fica em Pelotas, na Rua Santos Dumont, 324 — Centro. Daqui saem os projetos, laudos,
              avaliações, perícias e a assessoria que atendem produtores, empresas e famílias de Pelotas e de
              toda a Metade Sul do Rio Grande do Sul.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <a
                data-testid="municipality-wa-cta"
                href={waLink("Olá! Moro em Pelotas e gostaria de conversar sobre um serviço.")}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F9F8F3] px-7 py-4 text-sm font-semibold text-[#16220F] transition-colors duration-300 hover:bg-[#8FC690]"
              >
                Falar no WhatsApp
              </a>
              <a
                data-testid="municipality-maps"
                href={MAPS_LINK}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#F9F8F3]/30 px-7 py-4 text-sm font-semibold transition-colors duration-300 hover:border-[#8FC690]"
              >
                <MapPin className="h-4 w-4" /> Ver a sede no mapa
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Sede + mapa incorporado (lazy) */}
      <section data-testid="municipality-hq" className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 md:py-24">
        <Reveal><Overline>Sede e atendimento</Overline></Reveal>
        <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.05] tracking-tight text-[#1A2218] sm:text-5xl">
          Onde nos encontrar <em className="text-[#233D25]">em Pelotas.</em>
        </h2>
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="h-full rounded-[24px] border border-[#E5E0D5] bg-white p-7 sm:p-10">
              <div className="space-y-5 text-[#2C332A]">
                <p className="flex items-start gap-3"><MapPin className="mt-1 h-5 w-5 shrink-0 text-[#5D995E]" /><span>{CONTACT.address} · {CONTACT.city} · {CONTACT.cep}</span></p>
                <p className="flex items-start gap-3"><Phone className="mt-1 h-5 w-5 shrink-0 text-[#5D995E]" /><a data-testid="municipality-phone" href={`tel:+${CONTACT.wa}`} className="link-underline">{CONTACT.phone}</a></p>
                <p className="flex items-start gap-3"><Mail className="mt-1 h-5 w-5 shrink-0 text-[#5D995E]" /><a data-testid="municipality-email" href={`mailto:${CONTACT.email}`} className="link-underline">{CONTACT.email}</a></p>
                <p className="flex items-start gap-3"><Clock className="mt-1 h-5 w-5 shrink-0 text-[#5D995E]" /><span data-testid="municipality-hours">{CONTACT.hours}</span></p>
              </div>
              <p className="mt-8 text-sm leading-relaxed text-[#4A5549]">
                Atendimento presencial na sede de Pelotas e em visitas técnicas aos imóveis da região.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <iframe
              data-testid="municipality-map"
              title="Mapa da sede da Melo Agro Engenharia — Rua Santos Dumont, 324, Centro, Pelotas/RS"
              src={MAPS_EMBED}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-full min-h-[320px] w-full rounded-[24px] border border-[#E5E0D5]"
            />
          </Reveal>
        </div>
      </section>

      {/* 8 frentes com links */}
      <section className="bg-[#F2EFE9] py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal><Overline>Serviços disponíveis em Pelotas</Overline></Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={0.05 * i} className="h-full">
                <Link
                  to={`/${s.id}`}
                  data-testid={`municipality-service-${s.id}`}
                  className="group flex h-full flex-col justify-between rounded-[20px] border border-[#E5E0D5] bg-white p-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#233D25]"
                >
                  <h2 className="font-serif text-2xl leading-snug text-[#1A2218]">{s.title}</h2>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#233D25]">
                    Ver detalhes
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          {/* Cadeias + municípios vizinhos (sem links) */}
          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <div data-testid="municipality-cadeias" className="h-full rounded-[24px] bg-[#1B2F1D] p-7 text-[#F9F8F3] sm:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#8FC690]">Cadeias produtivas atendidas</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {CADEIAS.map((c) => (
                    <span key={c} className="rounded-full border border-[#8FC690]/40 px-3.5 py-1.5 text-sm text-[#F9F8F3]/90">{c}</span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="h-full rounded-[24px] border border-[#E5E0D5] bg-white p-7 sm:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#5D995E]">Municípios vizinhos atendidos</p>
                <div data-testid="municipality-neighbors" className="mt-6 flex flex-wrap gap-2">
                  {VIZINHOS.map((m) => (
                    <span key={m} className="rounded-full bg-[#F2EFE9] px-3.5 py-1.5 text-sm text-[#4A5549]">{m}</span>
                  ))}
                </div>
                <p className="mt-6 text-sm leading-relaxed text-[#4A5549]">
                  Além da sede, atendemos os municípios vizinhos da Metade Sul do RS — com visitas técnicas
                  agendadas diretamente pelo WhatsApp.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA WhatsApp + formulário curto */}
      <section className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 md:py-24">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><Overline>Fale com a gente</Overline></Reveal>
            <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight text-[#1A2218] sm:text-5xl">
              Sua demanda em Pelotas, <em className="text-[#5D995E]">sem burocracia.</em>
            </h2>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-[#4A5549] md:text-lg">
                Conte o que você precisa pelo formulário ou direto no WhatsApp — respondemos no horário
                comercial.
              </p>
              <a
                data-testid="municipality-wa-footer"
                href={waLink("Olá! Preciso de um serviço em Pelotas ou região.")}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#233D25] px-7 py-4 text-sm font-semibold text-[#F9F8F3] transition-colors duration-300 hover:bg-[#3E5B3F]"
              >
                WhatsApp {CONTACT.phone}
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-7">
            <form data-testid="municipality-form" onSubmit={submit} className="rounded-[28px] border border-[#E5E0D5] bg-white p-6 sm:p-10">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

              <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                <label className="group block border-b border-[#1A2218]/15 pb-2 transition-colors duration-300 focus-within:border-[#5D995E]">
                  <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#4A5549]">Nome *</span>
                  <input data-testid="municipality-name" required minLength={2} value={form.name} onChange={set("name")} className="mt-2 w-full bg-transparent text-base text-[#1A2218] outline-none placeholder:text-[#4A5549]/60" placeholder="Seu nome" />
                </label>
                <label className="group block border-b border-[#1A2218]/15 pb-2 transition-colors duration-300 focus-within:border-[#5D995E]">
                  <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#4A5549]">WhatsApp *</span>
                  <input data-testid="municipality-form-phone" required minLength={8} value={form.phone} onChange={set("phone")} className="mt-2 w-full bg-transparent text-base text-[#1A2218] outline-none placeholder:text-[#4A5549]/60" placeholder="(53) 99999-0000" />
                </label>
              </div>
              <label className="group mt-5 block border-b border-[#1A2218]/15 pb-2 transition-colors duration-300 focus-within:border-[#5D995E]">
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#4A5549]">Mensagem *</span>
                <textarea data-testid="municipality-message" required minLength={5} rows={3} value={form.message} onChange={set("message")} className="mt-2 w-full resize-none bg-transparent text-base text-[#1A2218] outline-none placeholder:text-[#4A5549]/60" placeholder="Conte brevemente sua necessidade" />
              </label>
              <label className="mt-5 flex items-start gap-3 text-sm text-[#4A5549]">
                <input type="checkbox" required checked={form.consent} onChange={set("consent")} data-testid="municipality-consent" className="mt-0.5 h-4 w-4 accent-[#233D25]" />
                <span>
                  Autorizo o contato da Melo Agro Engenharia e li a{" "}
                  <Link to="/politica-de-privacidade" className="font-semibold text-[#233D25] underline underline-offset-2">Política de Privacidade</Link>. *
                </span>
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="municipality-submit"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#233D25] px-7 py-4 font-semibold text-[#F9F8F3] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#3E5B3F] disabled:opacity-70"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                {loading ? "Enviando..." : "Enviar mensagem"}
              </button>
              {sent && (
                <p data-testid="municipality-success" className="mt-4 flex items-center gap-2 rounded-2xl bg-[#F2EFE9] p-4 text-sm font-semibold text-[#233D25]">
                  <CheckCircle2 className="h-4 w-4" /> Recebemos sua mensagem! Retornaremos em breve.
                </p>
              )}
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};